import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RG from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * RadioGroup primitive (Compose API over Radix UI).
 *
 * Use `<RadioGroup>` as the controller and `<RadioGroupItem>` for each option.
 * The optional sibling `<label>` should be wired with `htmlFor` to the item id.
 */
export const RadioGroup = forwardRef<
  ElementRef<typeof RG.Root>,
  ComponentPropsWithoutRef<typeof RG.Root>
>(function RadioGroup({ className, ...props }, ref) {
  return <RG.Root ref={ref} className={cn('grid gap-2', className)} {...props} />;
});

export const RadioGroupItem = forwardRef<
  ElementRef<typeof RG.Item>,
  ComponentPropsWithoutRef<typeof RG.Item>
>(function RadioGroupItem({ className, ...props }, ref) {
  return (
    <RG.Item
      ref={ref}
      className={cn(
        'aspect-square h-4 w-4 rounded-full border border-border bg-bg-input transition-colors ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
          'focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
          'data-[state=checked]:border-primary data-[state=checked]:bg-primary ' +
          'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RG.Indicator className="flex h-full w-full items-center justify-center text-white">
        <Circle size={8} className="fill-current" aria-hidden />
      </RG.Indicator>
    </RG.Item>
  );
});
